import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Auth } from 'aws-amplify';
import { AIAssistantWidget } from '@amzn/orion-components';
import { SparkleIcon } from '@amzn/orion-components/icons';
import '@amzn/orion-components/styles.css';
import { getAIAssistantConfig } from 'src/config/aiAssistantConfig';
import { useUserAlias } from 'src/common/hooks/useUserAlias';
import {
  fetchFeatureFlags,
  isFeatureEnabled,
  FeatureFlag,
  FeatureFlagMap,
  defaultFlags,
} from 'src/config/featureFlags';

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = [
    // Compact panel size (only when not expanded to fullscreen)
    `.floating-chat-panel:not(.floating-chat-panel--expanded) { width: 480px !important; height: min(600px, calc(100vh - 100px)) !important; }`,
    `.floating-chat-panel__icon { width: 32px !important; height: 32px !important; }`,
    `.floating-chat-panel__icon svg { width: 18px !important; height: 18px !important; }`,
    `.genai-welcome__hero-icon { width: 40px !important; height: 40px !important; margin-bottom: 8px !important; }`,
    `.genai-welcome__hero-title { font-size: 20px !important; }`,
    `.genai-welcome__hero-subtitle { font-size: 12px !important; }`,
    // FAB entry animation
    `.ai-fab--entry .ai-fab__icon svg { animation: iconHoverSpin 0.6s ease-out forwards, iconBreathing 3s ease-in-out 0.6s infinite; }`,
  ].join('\n');
  document.head.appendChild(style);
}

interface ChatWidgetContextValue {
  openChat: () => void;
  isEnabled: boolean;
}

const ChatWidgetContext = createContext<ChatWidgetContextValue>({
  openChat: () => {},
  isEnabled: false,
});

export const useChatWidget = () => useContext(ChatWidgetContext);

export const ChatWidgetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlagMap>(defaultFlags);
  const [isChatOpen, setIsChatOpen] = useState(
    () => sessionStorage.getItem('cia-chat-open') !== 'false'
  );
  const { alias } = useUserAlias();

  useEffect(() => {
    fetchFeatureFlags().then(setFlags);
  }, []);

  const isEnabled = isFeatureEnabled(flags, FeatureFlag.AI_ASSISTANT);

  const getAuthToken = useCallback(async () => {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  }, []);

  const handleClose = useCallback(() => {
    setIsChatOpen(false);
    sessionStorage.setItem('cia-chat-open', 'false');
  }, []);
  const openChat = useCallback(() => {
    setIsChatOpen(true);
    sessionStorage.setItem('cia-chat-open', 'true');
  }, []);

  const config = useMemo(
    () => ({
      ...getAIAssistantConfig(),
      getAuthToken,
      userAlias: alias,
      visibleToolbarActions: ['new-chat' as const],
    }),
    [getAuthToken, alias]
  );

  const contextValue = useMemo(() => ({ openChat, isEnabled }), [openChat, isEnabled]);

  return (
    <ChatWidgetContext.Provider value={contextValue}>
      {children}
      {isEnabled &&
        createPortal(
          <>
            {!isChatOpen && (
              <button
                onClick={openChat}
                aria-label="Open AI Assistant"
                className="ai-fab ai-fab--light ai-fab--entry"
                onMouseEnter={(e) => {
                  const svg = e.currentTarget.querySelector('.ai-fab__icon svg') as HTMLElement;
                  if (svg) svg.style.animation = 'iconHoverSpin 0.6s ease-out forwards';
                }}
                onMouseLeave={(e) => {
                  const svg = e.currentTarget.querySelector('.ai-fab__icon svg') as HTMLElement;
                  if (svg) svg.style.animation = 'iconBreathing 3s ease-in-out infinite';
                }}
              >
                <span className="ai-fab__shimmer" />
                <span className="ai-fab__icon">
                  <SparkleIcon size={28} weight="fill" />
                </span>
              </button>
            )}
            <AIAssistantWidget
              config={config}
              isOpen={isChatOpen}
              onClose={handleClose}
              theme="light"
            />
          </>,
          document.body
        )}
    </ChatWidgetContext.Provider>
  );
};
